import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {VehicleTypeService} from '../shared/vehicle-type.service';
import {ModalService} from '../../shared/services/modal.service';
import {LoaderService} from '../../shared/services/loader.service';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {VehicleType, VehicleTypeRegion} from '../shared/vehicle-type.model';
import {Region} from '../../user-dashboard/shared/region.model';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-decarbonization-vehicle-type-list',
  templateUrl: './decarbonization-vehicle-type-list.component.html',
  styleUrls: ['./decarbonization-vehicle-type-list.component.scss']
})
export class DecarbonizationVehicleTypeListComponent implements OnInit {

  public selfVehicleTypes: Array<VehicleType | VehicleTypeRegion> = [];
  public filteredVehicleTypes: Array<VehicleType> = [];
  public removableVehicleTypesId: Array<string> = [];

  public newVehicleType: string;
  public selectedVehicleType: VehicleType;

  @Input() public regions: Array<Region> = [];
  @Input() public canCreateDecarbonizationTypes = false;
  @Input() public canManageRegionDecarbonizationTypes = false;

  @ViewChild('vehicleTypeCreationModal') private vehicleTypeCreationModal: TemplateRef<any>;
  @ViewChild('regionVehicleTypeAddModal') private regionVehicleTypeAddModal: TemplateRef<any>;
  @ViewChild('vehicleTypeDeletionModal') private vehicleTypeDeletionModal: TemplateRef<any>;
  @ViewChild('vehicleTypeRegionRemovalModal') private vehicleTypeRegionRemovalModal: TemplateRef<any>;

  constructor(
    private loaderService: LoaderService,
    private modalService: ModalService,
    private vehicleTypeService: VehicleTypeService,
    private authService: AuthenticationService,
    private errorHandlerService: ErrorHandlerService,
    private toastrService: ToastrService,
    private translationService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.refreshListObservable().subscribe();
  }

  public openVehicleTypeCreationModal() {
    this.newVehicleType = '';
    this.modalService.open({
      titleKey: 'decarbonization.vehicle-types.creation-modal.title',
      content: this.vehicleTypeCreationModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.vehicleTypeService.createVehicleType(this.newVehicleType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.vehicle-types.error.creation');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.vehicle-types.creation-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  public openRegionVehicleTypeAddModal() {
    delete this.selectedVehicleType;

    const regionNumberByVehicleType = this.selfVehicleTypes.reduce(
      (record, regionVehicleType: VehicleTypeRegion) => {
        if (record.hasOwnProperty(regionVehicleType.vehicleTypeId)) {
          return {...record, [regionVehicleType.vehicleTypeId]: ++record[regionVehicleType.vehicleTypeId]}
        } else {
          return {...record, [regionVehicleType.vehicleTypeId]: 1}
        }
      },
      {}
    )

    return this.vehicleTypeService.getVehicleTypes()
      .pipe(
        map(vehicleTypes => vehicleTypes.filter(vehicleType => regionNumberByVehicleType[vehicleType.id] == null || regionNumberByVehicleType[vehicleType.id] < this.regions.length)),
        tap(filteredVehicleTypes => this.filteredVehicleTypes = filteredVehicleTypes),
        switchMap(() => this.modalService.open({
          titleKey: 'decarbonization.vehicle-types.region-add.title',
          content: this.regionVehicleTypeAddModal,
          validateButton: {
            textKey: 'buttons.validate',
          },
          closeButton: {
            textKey: 'buttons.cancel',
          },
          size: 'sm'
        })),
        switchMap(() => this.vehicleTypeService.addOwnRegionVehicleType(this.selectedVehicleType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.vehicle-types.error.add-region');
          return EMPTY;
        }),
      ).subscribe(
        () => {
          this.toastrService.success(
            this.translationService.instant('decarbonization.vehicle-types.region-add.success')
          );
          this.refreshListObservable().subscribe();
        }
      );
  }

  public openVehicleTypeDeletionModal(vehicleType: VehicleType) {
    this.modalService.open({
      titleKey: 'decarbonization.vehicle-types.deletion-modal.title',
      content: this.vehicleTypeDeletionModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.vehicleTypeService.deleteVehicleType(vehicleType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.vehicle-types.deletion-modal.error');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.vehicle-types.deletion-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  public openVehicleTypeRegionRemovalModal(vehicleTypeRegion: VehicleTypeRegion) {
    this.modalService.open({
      titleKey: 'decarbonization.vehicle-types.remove-region-modal.title',
      content: this.vehicleTypeRegionRemovalModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.vehicleTypeService.deleteOwnRegionVehicleType(vehicleTypeRegion)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.vehicle-types.remove-region-modal.error');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.vehicle-types.remove-region-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  private refreshListObservable() {
    this.loaderService.startLoad();
    return forkJoin([this.getVehicleTypeListObservable(), this.getRemovableVehicleTypeListObservable()])
      .pipe(
        finalize(() => this.loaderService.stopLoad())
      );
  }

  private getVehicleTypeListObservable () {
    let vehicleTypeListObservable: Observable<any[]>;
    if (this.canCreateDecarbonizationTypes) {
      vehicleTypeListObservable = this.vehicleTypeService.getVehicleTypes();
    } else if (this.canManageRegionDecarbonizationTypes) {
      vehicleTypeListObservable = this.vehicleTypeService.getSelfVehicleTypes();
    } else {
      throw Error('Cannot fetch vehicle types list');
    }

    return vehicleTypeListObservable.pipe(
      catchError((e: HttpErrorResponse) => {
        this.errorHandlerService.handleHttpError(e, 'decarbonization.vehicle-types.error.fetch');
        return EMPTY;
      }),
      tap(vehicleTypes => this.selfVehicleTypes = vehicleTypes)
    )
  }

  private getRemovableVehicleTypeListObservable() {
    let removableVehicleTypeListObservable: Observable<any[]>;
    if (this.canCreateDecarbonizationTypes) {
      removableVehicleTypeListObservable = this.vehicleTypeService.getRemovableVehicleTypeIds();
    } else if (this.canManageRegionDecarbonizationTypes) {
      removableVehicleTypeListObservable = this.vehicleTypeService.getSelfRemovableVehicleTypeRegionIds();
    } else {
      removableVehicleTypeListObservable = of([]);
    }

    return removableVehicleTypeListObservable.pipe(
      tap(removableVehicleTypes => this.removableVehicleTypesId = removableVehicleTypes)
    );
  }
}
